/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { newGuid } from '../Utilities';
import { FeatureSwitchContext } from './FeatureSwitchContext';
import { FeatureKeys, featureSwitchService } from '.';

interface IFeatureSwitchProviderProps {
  enabledByDefault?: FeatureKeys[];
  children: React.ReactNode;
}

export type HashMap<T> = Record<string, T>;

type FeatureSwitchProviderProps = IFeatureSwitchProviderProps;

const applyEnabledByDefault = (enabledByDefault: FeatureKeys[]): void => {
  if (enabledByDefault.length === 0) return;
  const map: HashMap<boolean> = {};
  enabledByDefault.forEach((featureKey) => {
    map[featureKey] = true;
  });
  featureSwitchService.setFeaturesFromMap(map);
};

export const FeatureSwitchProvider: React.FunctionComponent<FeatureSwitchProviderProps> = ({
  children,
  enabledByDefault = [],
}: React.PropsWithChildren<FeatureSwitchProviderProps>): React.ReactElement<FeatureSwitchProviderProps> => {
  // The defaults are applied before the first render instead of in an effect. Changing `key`
  // remounts the whole app below this provider, so setting it on mount (and again whenever a new
  // `enabledByDefault` array arrived) rebuilt every page two or three times per load, repeating
  // each page's mount-time API calls.
  const [key, setKey] = useState<string>(() => {
    applyEnabledByDefault(enabledByDefault);
    return newGuid();
  });
  const isFirstRun = useRef(true);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    if (enabledByDefault.length > 0) {
      applyEnabledByDefault(enabledByDefault);
      setKey(newGuid());
    }
  }, [enabledByDefault]);

  const featureIsEnabled = useCallback(
    (featureKey: FeatureKeys): boolean => featureSwitchService.featureIsEnabled(featureKey),
    []
  );

  const setFeaturesFromMap = useCallback((features: HashMap<boolean>) => {
    featureSwitchService.setFeaturesFromMap(features);
    setKey(newGuid());
  }, []);

  const renderElement = useCallback(
    (): React.ReactElement<FeatureSwitchProviderProps> => (
      <FeatureSwitchContext.Provider
        key={key}
        value={{
          featureIsEnabled,
          setFeaturesFromMap,
        }}>
        {children}
      </FeatureSwitchContext.Provider>
    ),
    [children, featureIsEnabled, key, setFeaturesFromMap]
  );

  return renderElement();
};
